import React from 'react';
import { Icon } from '../Icon';
import classNames from 'classnames';

export const generateInput = ({ size, className } = {}) => props => {
  const baseClass = !!props.minSearchLength ? 'form-control' : 'form-select';
  return (
    <input
      {...props.inputProps}
      className={classNames(baseClass, className, {
        'form-control--search': !!props.minSearchLength,
        [`${baseClass}-sm`]: size === 'sm',
        'is-empty': !props.value,
        'is-invalid': props.invalid,
      })}
    />
  );
};

export const generateSingleSelectionsContainer = ({
  size,
  className,
} = {}) => ({ disabled, selections, input, value }) => (
  <div
    className={classNames('kinetic-typeahead', className, {
      'kinetic-typeahead-sm': size === 'sm',
      'is-empty': !value,
      disabled,
    })}
  >
    {selections}
    {input}
  </div>
);

export const generateMultipleSelectionsContainer = ({
  size,
  className,
} = {}) => ({ disabled, selections, input }) => (
  <div
    className={classNames(
      'kinetic-typeahead kinetic-typeahead--multi',
      className,
      {
        'kinetic-typeahead-sm': size === 'sm',
      },
    )}
  >
    {selections}
    {!disabled && input}
  </div>
);

export const generateSingleSelection = (
  selectionToValue,
  { size, className, title } = {},
) => ({
  selection,
  disabled,
  edit,
  focusRef,
  remove,
  placeholder,
  id,
  invalid,
  form,
  minSearchLength,
}) => {
  const baseClass = !!minSearchLength ? 'form-control' : 'form-select';
  return (
    <div className="field-floating">
      <input
        type="text"
        id={id}
        form={form}
        disabled={disabled}
        placeholder={placeholder}
        className={classNames(baseClass, className, {
          'form-control--search': !disabled && !!minSearchLength,
          [`${baseClass}-sm`]: size === 'sm',
          'has-btn': selection && !disabled,
          'is-invalid': invalid,
        })}
        ref={focusRef}
        defaultValue={selection ? selectionToValue(selection) : ''}
        title={title && selection ? selectionToValue(selection) : ''}
        onFocus={() => {
          if (!selection) {
            // If no selection, start edit mode immediately
            edit(new MouseEvent('click'));
          } else {
            // If selection exists, select value to mimic other text fields
            focusRef.current.select();
          }
        }}
        onKeyDown={e => {
          if (['Backspace', 'Delete'].includes(e.key)) {
            // On backspace or delete remove the value and start edit mode
            remove();
            edit(new MouseEvent('click'));
          } else if (e.key.length === 1 && !e.ctrlKey) {
            // On click of any single character key (without ctrl), start edit mode
            edit(new MouseEvent('click'));
          } else if (
            !e.ctrlKey &&
            ![
              'Tab',
              'Home',
              'End',
              'Enter',
              'ArrowLeft',
              'ArrowRight',
              'ArrowUp',
              'ArrowDown',
            ].includes(e.key)
          ) {
            // On any other key besides the above ones and if ctrl is not pressed, do nothing
            e.preventDefault();
          }
        }}
      />
      {selection &&
        !disabled && (
          <button
            type="button"
            className={classNames(
              'btn btn-link-primary btn-floating',
              className,
              { 'btn-sm': size === 'sm' },
            )}
            onClick={remove}
            aria-label="Clear Value"
            title="Clear Value"
          >
            <Icon name="x" md />
          </button>
        )}
    </div>
  );
};

export const generateMultipleSelection = (
  selectionToValue,
  { size, className } = {},
) => ({ disabled, selection, remove, id }) => (
  <div className="field-floating">
    <input
      type="text"
      id={id}
      disabled={true}
      className={classNames('form-control', className, {
        'form-control-sm': size === 'sm',
        'has-btn': !disabled,
      })}
      value={selectionToValue(selection)}
    />
    {!disabled && (
      <button
        type="button"
        className={classNames('btn btn-link-primary btn-floating', className, {
          'btn-sm': size === 'sm',
        })}
        onClick={remove}
        aria-label="Clear Value"
        title="Clear Value"
      >
        <Icon name="x" md />
      </button>
    )}
  </div>
);

export const SuggestionsContainer = ({ open, children, containerProps }) => (
  <div {...containerProps} className={classNames('suggestions', { open })}>
    {children}
  </div>
);

export const SuggestionAction = ({ suggestionValue, active }) => (
  <div className={classNames('suggestion', { active })}>
    <div className="link">{suggestionValue}</div>
  </div>
);

export const Status = props => (
  <div className="status__wrapper">
    {props.meta && (
      <div className="status meta">
        {props.meta}
        {props.clearFilterField && (
          <button
            className="btn btn-link-primary btn-sm"
            onClick={props.clearFilterField}
            aria-label="Clear Filter Field"
            title="Clear Filter Field"
          >
            <Icon name="x" md />
          </button>
        )}
      </div>
    )}
    {props.info && <div className="status info">{props.info}</div>}
    {props.warning && (
      <div className="status warning">
        <span className="with-icon">
          <Icon name="alert-triangle" sm />
          <span>{props.warning}</span>
        </span>
      </div>
    )}
    {props.filterFieldOptions && (
      <ul className="search-actions">
        {props.filterFieldOptions.map(({ count, label, onClick, value }, i) => (
          <li
            className="suggestion"
            key={i}
            role="button"
            tabIndex={0}
            onClick={onClick}
          >
            <Icon name="search" sm />
            <span>
              See <strong>{label}</strong> results for "{value}"
            </span>
            <span className="count">{count}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);
